/**
 * Timeline Service
 *
 * Handles all timeline/bitacora related operations for projects.
 * Provides unified view of project events from multiple sources.
 *
 * @module services/timelineService
 */

import { supabase } from './supabase';
import { formatDistanceToNow, differenceInDays, format as dfFormat } from 'date-fns';
import { es as esLocale } from 'date-fns/locale';
// Bucket `receipts`: en la base va el path, la signed URL se firma en cada
// lectura. Único resolvedor del repo — no duplicar.
import { resolveAttachmentUrl } from './purchaseService';
// Bucket `chat-attachments`: mismo trato desde que dejó de ser público (bajaba
// fotos con curl sin apikey). `chat_bitacora_links.attachment_urls` guarda el
// path y la bitácora lo firma acá, al leer.
import { resolveChatAttachmentUrl, chatAttachmentPath } from './chatService';

/**
 * Firma la foto de los eventos `chat_linked`.
 *
 * El bucket `chat-attachments` pasó a privado, así que la `photo_url` que la
 * vista/RPC trae de `chat_bitacora_links.attachment_urls` ya no es navegable
 * por sí sola: hay que firmarla o la bitácora pinta un <img> roto. Es el
 * mismo tratamiento que `getTaskTimeline` ya le da al comprobante de gasto
 * (`receipts`), y por la misma razón.
 *
 * Best-effort: los eventos que no son `chat_linked`, y los que no tienen un
 * path derivable (URL externa), pasan intactos.
 *
 * @param {Array<Object>} events
 * @returns {Promise<Array<Object>>}
 */
const resolveChatEventPhotos = async (events) => {
  if (!events?.length) return events || [];
  const needsSigning = events.some(
    (e) => e?.event_type === 'chat_linked' && chatAttachmentPath(e.photo_url)
  );
  if (!needsSigning) return events;

  return Promise.all(
    events.map(async (event) => {
      if (event?.event_type !== 'chat_linked' || !chatAttachmentPath(event.photo_url)) {
        return event;
      }
      return { ...event, photo_url: await resolveChatAttachmentUrl(event.photo_url) };
    })
  );
};

/**
 * Event type definitions for timeline
 */
export const TIMELINE_EVENT_TYPES = {
  // Project history events
  STATUS_CHANGE: 'status_change',
  PO_ADDED: 'po_added',
  PO_UPDATED: 'po_updated',
  PROJECT_CREATED: 'project_created',
  AMOUNT_UPDATED: 'amount_updated',
  CLIENT_CHANGED: 'client_changed',
  DATES_CHANGED: 'dates_changed',
  DESCRIPTION_UPDATED: 'description_updated',
  // Manual entries
  MANUAL_NOTE: 'manual_note',
  MILESTONE_REACHED: 'milestone_reached',
  CUSTOM_UPDATE: 'custom_update',
  // Other sources
  EXPENSE_RECORDED: 'expense_recorded',
  PHOTO_UPLOADED: 'photo_uploaded',
  QUOTATION_CREATED: 'quotation_created',
  QUOTATION_APPROVED: 'quotation_approved',
  // Task activity events (unified timeline)
  TASK_CREATED: 'task_created',
  TASK_COMPLETED: 'task_completed',
  STATUS_CHANGED: 'status_changed',
  COMMENT_ADDED: 'comment_added',
  ASSIGNED: 'assigned',
  PRIORITY_CHANGED: 'priority_changed',
  // Chat integration
  CHAT_LINKED: 'chat_linked',
};

/**
 * Event type labels (Spanish)
 */
export const EVENT_TYPE_LABELS = {
  status_change: 'Cambio de Estado',
  po_added: 'PO Asignado',
  po_updated: 'PO Actualizado',
  project_created: 'Proyecto Creado',
  amount_updated: 'Monto Actualizado',
  client_changed: 'Cliente Cambiado',
  dates_changed: 'Fechas Cambiadas',
  description_updated: 'Descripcion Actualizada',
  manual_note: 'Nota Manual',
  milestone_reached: 'Hito Alcanzado',
  custom_update: 'Actualizacion',
  expense_recorded: 'Gasto Registrado',
  photo_uploaded: 'Foto Subida',
  quotation_created: 'Cotizacion Creada',
  quotation_approved: 'Cotizacion Aprobada',
  // Task activity labels
  task_created: 'Tarea Creada',
  task_completed: 'Tarea Completada',
  status_changed: 'Estado Cambiado',
  comment_added: 'Comentario',
  assigned: 'Asignacion',
  priority_changed: 'Prioridad Cambiada',
  // Chat integration
  chat_linked: 'Chat Vinculado',
};

/**
 * Event type icons (Bootstrap Icons)
 */
export const EVENT_TYPE_ICONS = {
  status_change: 'bi-arrow-repeat',
  po_added: 'bi-file-earmark-text',
  po_updated: 'bi-file-earmark-text',
  project_created: 'bi-plus-circle',
  amount_updated: 'bi-currency-dollar',
  client_changed: 'bi-person-badge',
  dates_changed: 'bi-calendar-event',
  description_updated: 'bi-pencil',
  manual_note: 'bi-chat-left-text',
  milestone_reached: 'bi-flag-fill',
  custom_update: 'bi-info-circle',
  expense_recorded: 'bi-receipt',
  photo_uploaded: 'bi-camera',
  quotation_created: 'bi-file-text',
  quotation_approved: 'bi-check-circle',
  // Task activity icons
  task_created: 'bi-plus-square',
  task_completed: 'bi-check-square',
  status_changed: 'bi-arrow-left-right',
  comment_added: 'bi-chat-left-text',
  assigned: 'bi-person-plus',
  priority_changed: 'bi-exclamation-triangle',
  // Chat integration
  chat_linked: 'bi-chat-left-quote',
};

/**
 * Event type colors (Bootstrap variants)
 */
export const EVENT_TYPE_COLORS = {
  status_change: 'primary',
  po_added: 'info',
  po_updated: 'info',
  project_created: 'success',
  amount_updated: 'warning',
  client_changed: 'secondary',
  dates_changed: 'secondary',
  description_updated: 'secondary',
  manual_note: 'light',
  milestone_reached: 'warning',
  custom_update: 'light',
  expense_recorded: 'danger',
  photo_uploaded: 'success',
  quotation_created: 'primary',
  quotation_approved: 'success',
  // Task activity colors
  task_created: 'success',
  task_completed: 'success',
  status_changed: 'primary',
  comment_added: 'info',
  assigned: 'primary',
  priority_changed: 'warning',
  // Chat integration
  chat_linked: 'info',
};

/**
 * Get project timeline with filters
 * @param {string} projectId - Project ID
 * @param {Object} filters - Filter options
 * @param {string[]} filters.eventTypes - Filter by event types
 * @param {Date|string} filters.startDate - Start date filter
 * @param {Date|string} filters.endDate - End date filter
 * @param {boolean} filters.milestonesOnly - Show only milestones
 * @param {string} filters.search - Search in descriptions
 * @param {number} filters.limit - Max results (default 100)
 * @param {number} filters.offset - Offset for pagination
 * @returns {Promise<{data, error}>}
 */
export const getProjectTimeline = async (projectId, filters = {}) => {
  try {
    const {
      eventTypes = null,
      startDate = null,
      endDate = null,
      milestonesOnly = false,
      search = null,
      limit = 100,
      offset = 0,
    } = filters;

    // Use the database function for optimized query
    const { data, error } = await supabase.rpc('get_full_project_timeline', {
      p_project_id: projectId,
      p_event_types: eventTypes,
      p_start_date: startDate,
      p_end_date: endDate,
      p_milestones_only: milestonesOnly,
      p_limit: limit,
      p_offset: offset,
    });

    if (error) throw error;

    // Apply client-side search filter if provided
    let filteredData = await resolveChatEventPhotos(data || []);
    if (search) {
      const searchLower = search.toLowerCase();
      filteredData = filteredData.filter(event =>
        event.description?.toLowerCase().includes(searchLower) ||
        event.user_name?.toLowerCase().includes(searchLower)
      );
    }

    // Group events by date for display
    const groupedByDate = groupEventsByDate(filteredData);

    return {
      data: {
        events: filteredData,
        groupedByDate,
        total: filteredData.length,
      },
      error: null,
    };
  } catch (error) {
    console.error('Get project timeline error:', error);
    return { data: null, error };
  }
};

/**
 * Get timeline directly from view (alternative to RPC function)
 * @param {string} projectId - Project ID
 * @param {Object} filters - Filter options
 * @param {string} [filters.taskId] - Only include events scoped to this task
 * @returns {Promise<{data, error}>}
 */
export const getProjectTimelineDirect = async (projectId, filters = {}) => {
  try {
    const {
      eventTypes = null,
      startDate = null,
      endDate = null,
      milestonesOnly = false,
      taskId = null,
      limit = 100,
      offset = 0,
    } = filters;

    let query = supabase
      .from('project_timeline')
      .select('*')
      .eq('project_id', projectId)
      .order('event_timestamp', { ascending: false })
      .range(offset, offset + limit - 1);

    if (eventTypes && eventTypes.length > 0) {
      query = query.in('event_type', eventTypes);
    }

    if (startDate) {
      query = query.gte('event_timestamp', startDate);
    }

    if (endDate) {
      query = query.lte('event_timestamp', endDate);
    }

    if (milestonesOnly) {
      query = query.eq('is_milestone', true);
    }

    if (taskId) {
      query = query.filter('metadata->>task_id', 'eq', taskId);
    }

    const { data, error } = await query;

    if (error) throw error;

    // Fetch user info separately
    const userIds = [...new Set(data.map(e => e.user_id).filter(Boolean))];
    let usersMap = {};

    if (userIds.length > 0) {
      const { data: users } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);

      usersMap = users?.reduce((acc, u) => {
        acc[u.id] = u;
        return acc;
      }, {}) || {};
    }

    // Enrich events with user info and normalize timestamp field
    const enrichedData = await resolveChatEventPhotos(
      data.map(event => ({
        ...event,
        timestamp: event.event_timestamp, // Alias for grouping function
        user_name: usersMap[event.user_id]?.full_name || 'Usuario',
        user_email: usersMap[event.user_id]?.email || null,
      }))
    );

    const groupedByDate = groupEventsByDate(enrichedData);

    return {
      data: {
        events: enrichedData,
        groupedByDate,
        total: enrichedData.length,
      },
      error: null,
    };
  } catch (error) {
    console.error('Get project timeline direct error:', error);
    return { data: null, error };
  }
};

/**
 * Get timeline events scoped to a single task.
 * Combines task_activity + progress_photo events (filtered by metadata.task_id)
 * with expenses linked directly to the task via expenses.task_id.
 *
 * @param {string} projectId - Project ID (for the task)
 * @param {string} taskId - Task ID to filter by
 * @returns {Promise<{data, error}>}
 */
export const getTaskTimeline = async (projectId, taskId) => {
  try {
    const [timelineRes, expensesRes] = await Promise.all([
      supabase
        .from('project_timeline')
        .select('*')
        .eq('project_id', projectId)
        .filter('metadata->>task_id', 'eq', taskId)
        .order('event_timestamp', { ascending: false })
        .limit(200),
      supabase
        .from('expenses')
        // #705 — se pide expense_date (queda en metadata.expense_date para
        // el detalle expandido, que sigue mostrando "a qué día se imputa")
        // pero created_at es lo que decide event_timestamp y el orden: es
        // el mismo fix que la vista project_timeline, aplicado acá porque
        // esta consulta arma sus propios eventos de gasto en vez de leer
        // la vista para el caso scoped a una tarea.
        .select('id, project_id, amount, vendor, category, notes, receipt_url, expense_date, created_by, created_at')
        .eq('task_id', taskId)
        .order('created_at', { ascending: false })
        .limit(200),
    ]);

    if (timelineRes.error) throw timelineRes.error;
    if (expensesRes.error) throw expensesRes.error;

    const timelineEvents = await resolveChatEventPhotos(timelineRes.data || []);
    // `expenses.receipt_url` guarda el PATH del objeto en el bucket privado
    // `receipts`, no una URL navegable: hay que firmarla acá o la timeline
    // pinta un href roto. Se reusa el mismo resolvedor que el resto del repo
    // (`purchaseService.resolveAttachmentUrl`) en vez de re-implementarlo.
    const expenseEvents = await Promise.all(
      (expensesRes.data || []).map(async (e) => {
        const receiptUrl = await resolveAttachmentUrl(e.receipt_url);
        return {
          source: 'expense',
          id: e.id,
          project_id: e.project_id,
          event_type: 'expense_recorded',
          description: `Gasto: ${e.vendor || e.category} - $${e.amount}`,
          user_id: e.created_by,
          // #705 — created_at es "cuando pasó esto" (lo que la bitácora
          // fecha y ordena); expense_date es el día contable y va en
          // metadata para quien lo necesite, no en event_timestamp.
          event_timestamp: e.created_at,
          is_milestone: false,
          photo_url: receiptUrl,
          amount: e.amount,
          metadata: {
            task_id: taskId,
            category: e.category,
            notes: e.notes,
            expense_date: e.expense_date,
            receipt_url: receiptUrl,
            expandable: true,
          },
        };
      })
    );

    const allEvents = [...timelineEvents, ...expenseEvents].sort(
      (a, b) => new Date(b.event_timestamp) - new Date(a.event_timestamp)
    );

    // Enrich with user names
    const userIds = [...new Set(allEvents.map((e) => e.user_id).filter(Boolean))];
    let usersMap = {};
    if (userIds.length > 0) {
      const { data: users } = await supabase
        .from('profiles')
        .select('id, full_name')
        .in('id', userIds);
      usersMap = (users || []).reduce((acc, u) => {
        acc[u.id] = u;
        return acc;
      }, {});
    }

    const enriched = allEvents.map((event) => ({
      ...event,
      timestamp: event.event_timestamp,
      user_name: usersMap[event.user_id]?.full_name || 'Sistema',
    }));

    return { data: { events: enriched, total: enriched.length }, error: null };
  } catch (error) {
    console.error('Get task timeline error:', error);
    return { data: null, error };
  }
};

/**
 * Add manual entry to timeline
 * @param {string} projectId - Project ID
 * @param {Object} eventData - Event data
 * @param {string} eventData.description - Event description
 * @param {string} eventData.changeType - Type of entry (manual_note, milestone_reached, custom_update)
 * @param {boolean} eventData.isMilestone - Whether this is a milestone
 * @returns {Promise<{data, error}>}
 */
export const addManualEvent = async (projectId, eventData) => {
  try {
    const {
      description,
      changeType = 'manual_note',
      isMilestone = false,
    } = eventData;

    if (!description?.trim()) {
      throw new Error('La descripcion es requerida');
    }

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }

    // Map frontend change types to valid database types
    // Note: DB only supports specific types, we use 'description_updated' for manual entries
    const dbChangeTypeMap = {
      'manual_note': 'description_updated',
      'milestone_reached': 'description_updated',
      'custom_update': 'description_updated',
    };

    const dbChangeType = dbChangeTypeMap[changeType] || 'description_updated';

    // Prefix description with type indicator for manual entries
    const prefix = isMilestone || changeType === 'milestone_reached' ? '[HITO] ' :
                   changeType === 'manual_note' ? '[NOTA] ' : '';

    const { data, error } = await supabase
      .from('project_history')
      .insert({
        project_id: projectId,
        changed_by: user.id,
        change_type: dbChangeType,
        new_value: prefix + description.trim(),
        notes: description.trim(),
      })
      .select('id')
      .single();

    if (error) throw error;

    return { data: { id: data.id }, error: null };
  } catch (error) {
    console.error('Add manual event error:', error);
    return { data: null, error };
  }
};

/**
 * Get timeline summary/statistics
 * @param {string} projectId - Project ID
 * @returns {Promise<{data, error}>}
 */
export const getTimelineSummary = async (projectId) => {
  try {
    const { data, error } = await supabase.rpc('get_project_timeline_summary', {
      p_project_id: projectId,
    });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Get timeline summary error:', error);
    return { data: null, error };
  }
};

/**
 * Group events by date for display
 * @param {Array} events - Array of timeline events
 * @returns {Object} Events grouped by date string
 */
const groupEventsByDate = (events) => {
  const grouped = {};

  events.forEach(event => {
    const date = new Date(event.timestamp);
    const dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD

    if (!grouped[dateKey]) {
      grouped[dateKey] = {
        date: dateKey,
        displayDate: formatDateHeader(date),
        events: [],
      };
    }

    grouped[dateKey].events.push(event);
  });

  // Convert to array and sort by date descending
  return Object.values(grouped).sort((a, b) =>
    new Date(b.date) - new Date(a.date)
  );
};

/**
 * Format date for display in group headers
 * @param {Date} date - Date object
 * @returns {string} Formatted date string
 */
const formatDateHeader = (date) => {
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const yesterdayOnly = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());

  if (dateOnly.getTime() === todayOnly.getTime()) {
    return 'Hoy';
  }

  if (dateOnly.getTime() === yesterdayOnly.getTime()) {
    return 'Ayer';
  }

  return date.toLocaleDateString('es-CR', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

/**
 * Get available event types for filtering
 * @returns {Array} Array of event type options
 */
export const getEventTypeOptions = () => {
  return Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => ({
    value,
    label,
    icon: EVENT_TYPE_ICONS[value],
    color: EVENT_TYPE_COLORS[value],
  }));
};

/**
 * Get detailed information for an expandable timeline event
 * @param {string} eventId - Event ID
 * @param {string} source - Event source (task_activity, expense, photo, etc.)
 * @returns {Promise<{data, error}>}
 */
export const getEventDetails = async (eventId, source) => {
  try {
    const { data, error } = await supabase.rpc('get_timeline_event_details', {
      p_event_id: eventId,
      p_source: source,
    });

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('Get event details error:', error);
    return { data: null, error };
  }
};

// =====================================================================
// Feed / Mural classification (Tier A/B/C)
// =====================================================================

const TIER_A_TYPES = new Set([
  'photo_uploaded',
  'comment_added',
  'manual_note',
  'milestone_reached',
]);

const TIER_B_TYPES = new Set([
  'expense_recorded',
  'quotation_created',
  'quotation_approved',
  'chat_linked',
  'task_created',
  'task_completed',
  'assigned',
]);

/**
 * Decide which tier an event belongs to.
 * Milestones always float up to Tier A regardless of event type.
 * A chat message shared to the bitacora that carries photo evidence also
 * floats up to Tier A — a shared photo is meant to be visible proof, not a
 * one-line mention (see issue #267). Plain text-only shares stay Tier B.
 * @param {Object} event - Timeline event
 * @returns {'A'|'B'|'C'}
 */
export const getTierForEvent = (event) => {
  if (!event) return 'C';
  if (event.is_milestone) return 'A';
  if (event.event_type === 'chat_linked' && event.photo_url) return 'A';
  if (TIER_A_TYPES.has(event.event_type)) return 'A';
  if (TIER_B_TYPES.has(event.event_type)) return 'B';
  return 'C';
};

/**
 * Format a currency amount for humanized copy.
 * Falls back to raw value if not a number.
 */
const formatAmount = (amount) => {
  if (amount === null || amount === undefined || amount === '') return '';
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount);
  return new Intl.NumberFormat('es-CR', {
    style: 'currency',
    currency: 'CRC',
    maximumFractionDigits: 0,
  }).format(n);
};

/**
 * Turn a raw event into a human-friendly action phrase for the feed.
 * Example: status_change (draft → active) → "activó el proyecto"
 * @param {Object} event - Timeline event
 * @returns {string}
 */
export const humanizeAction = (event) => {
  if (!event) return '';
  const type = event.event_type;
  const meta = event.metadata || {};
  const amount = event.amount;

  switch (type) {
    case 'photo_uploaded':
      return 'subió una foto';
    case 'comment_added':
      return 'comentó';
    case 'manual_note':
      return 'añadió una nota';
    case 'milestone_reached':
      return 'registró un hito';

    case 'expense_recorded':
      return amount ? `registró un gasto de ${formatAmount(amount)}` : 'registró un gasto';
    case 'quotation_created':
      return amount ? `creó una cotización por ${formatAmount(amount)}` : 'creó una cotización';
    case 'quotation_approved':
      return amount ? `aprobó una cotización por ${formatAmount(amount)}` : 'aprobó una cotización';
    case 'chat_linked':
      return event.photo_url ? 'compartió una foto del chat' : 'vinculó una conversación';
    case 'task_created':
      return 'creó una tarea';
    case 'task_completed':
      return 'completó una tarea';
    case 'assigned':
      return 'asignó una tarea';

    case 'status_change': {
      const newStatus = meta.new_status || meta.new_value;
      if (newStatus === 'active') return 'activó el proyecto';
      if (newStatus === 'completed') return 'marcó el proyecto como completado';
      if (newStatus === 'quoted') return 'marcó el proyecto como cotizado';
      if (newStatus === 'cancelled') return 'canceló el proyecto';
      if (newStatus) return `cambió el estado a "${newStatus}"`;
      return 'cambió el estado';
    }
    case 'status_changed': {
      const newStatus = meta.new_status;
      if (newStatus) return `cambió una tarea a "${newStatus}"`;
      return 'cambió el estado de una tarea';
    }
    case 'po_added':
      return 'asignó una orden de compra';
    case 'po_updated':
      return 'actualizó una orden de compra';
    case 'project_created':
      return 'creó el proyecto';
    case 'amount_updated':
      return 'actualizó el monto';
    case 'client_changed':
      return 'cambió el cliente';
    case 'dates_changed':
      return 'cambió las fechas';
    case 'description_updated':
      return 'actualizó la descripción';
    case 'priority_changed':
      return 'cambió la prioridad';
    case 'custom_update':
      return 'realizó una actualización';

    default:
      return EVENT_TYPE_LABELS[type] || 'realizó una acción';
  }
};

/**
 * Format a timestamp for the feed (relative within a week, absolute after).
 * @param {string|Date} timestamp
 * @returns {string}
 */
export const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';

  const now = new Date();
  const days = differenceInDays(now, date);

  if (days < 7) {
    return formatDistanceToNow(date, { locale: esLocale, addSuffix: true });
  }

  // Same year → "8 feb"; different year → "8 feb 2025"
  const sameYear = date.getFullYear() === now.getFullYear();
  return dfFormat(date, sameYear ? 'd MMM' : 'd MMM yyyy', { locale: esLocale });
};

/**
 * Classify events into feed items and group Tier C runs (same user + same day)
 * into a single collapsed group. Inserts date headers ("Hoy", "Ayer", etc.)
 * between day transitions.
 *
 * Input events must already be sorted DESC by timestamp (newest first), which
 * is what getProjectTimelineDirect returns.
 *
 * @param {Array<Object>} events
 * @returns {Array<{kind: 'date_header'|'post'|'mini'|'group', ...}>}
 */
export const classifyAndGroupEvents = (events) => {
  const result = [];
  if (!Array.isArray(events) || events.length === 0) return result;

  let currentDate = null;

  for (const event of events) {
    const ts = event.timestamp || event.event_timestamp;
    if (!ts) continue;
    const date = new Date(ts);
    const dateKey = date.toISOString().split('T')[0];

    if (dateKey !== currentDate) {
      currentDate = dateKey;
      result.push({
        kind: 'date_header',
        date: dateKey,
        displayDate: formatDateHeader(date),
      });
    }

    const tier = getTierForEvent(event);

    if (tier === 'A') {
      result.push({ kind: 'post', event });
    } else if (tier === 'B') {
      result.push({ kind: 'mini', event });
    } else {
      const last = result[result.length - 1];
      const canMerge =
        last &&
        last.kind === 'group' &&
        last.date === dateKey &&
        last.user_id === event.user_id;

      if (canMerge) {
        last.events.push(event);
      } else {
        result.push({
          kind: 'group',
          date: dateKey,
          user_id: event.user_id,
          user_name: event.user_name,
          events: [event],
          timestamp: ts,
        });
      }
    }
  }

  return result;
};

const timelineService = {
  getProjectTimeline,
  getProjectTimelineDirect,
  getTaskTimeline,
  addManualEvent,
  getTimelineSummary,
  getEventTypeOptions,
  getEventDetails,
  getTierForEvent,
  humanizeAction,
  formatRelativeTime,
  classifyAndGroupEvents,
  TIMELINE_EVENT_TYPES,
  EVENT_TYPE_LABELS,
  EVENT_TYPE_ICONS,
  EVENT_TYPE_COLORS,
};

export default timelineService;
